// src/pages/index.js (Dashboard) — MUI-based dropdown (stable)
import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Button,
  Container,
  Menu,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { ColorModeContext } from "../../index"; // keep your export path

// Import Components (keep your existing components)
import MiniWidgets from "./MiniWidgets";
import RevenueAnalytics from "./RevenueAnalytics";
// import SalesAnalytics from "./SalesAnalytics";
import BlockedPolicies from "./BlockedPolicies";
import EarningReports from "./EarningReports";
import PolicyViolationTrends from "./PolicyViolationTrends";
import RiskAnalysis from "./RiskAnalysis";
import AdsBlock from "./AdsBlock";
import PeakHours from "./PeakHours";
import TopAccessedDomains from "./TopDomains"
import CustomChartsWidget from "./CustomChartsWidget";

const LOCALSTORAGE_KEY = "dashboardWidgetVisibility";

const Dashboard = () => {
  // default visibility (normalized keys are camelCase)
  const defaultVisibility = {
    miniWidgets: true,
    
    // salesAnalytics: true,
    // earningReports: false,
    recentlyActivity: false,
    latestTransactions: false,
    riskAnalysis: true,
    revenueAnalytics: false,
    blockedPolicies: true,
    PeakHours: true,
    TopAccessedDomains: true,
    customCharts: true,
    // adsBlock: true,
  };

  const [visibility, setVisibility] = useState(defaultVisibility);
  const [showDialog, setShowDialog] = useState(false);
  const { mode } = useContext(ColorModeContext || { mode: "light" });

  // MUI Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  // responsive columns: 3 / 2 / 1 based on width
  const getColsFromWidth = (w) => (w >= 1200 ? 3 : w >= 768 ? 2 : 1);
  const [columns, setColumns] = useState(
    typeof window !== "undefined" ? getColsFromWidth(window.innerWidth) : 3
  );

  useEffect(() => {
    // load or initialize visibility
    try {
      const raw = localStorage.getItem(LOCALSTORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        setVisibility({ ...defaultVisibility, ...parsed });
      } else {
        localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(defaultVisibility));
      }
    } catch (err) {
      console.error("Error reading widget visibility from localStorage:", err);
      setVisibility(defaultVisibility);
      localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(defaultVisibility));
    }

    // responsive column changes
    const onResize = () => setColumns(getColsFromWidth(window.innerWidth));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // show dialog once after 1 minute (if never shown)
  useEffect(() => {
    const lastShown = localStorage.getItem("lastDialogShown");
    if (!lastShown) {
      const timer = setTimeout(() => {
        setShowDialog(true);
        localStorage.setItem("lastDialogShown", Date.now().toString());
      }, 60000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleCloseDialog = () => setShowDialog(false);

  const handleToggle = (key) => {
    const next = { ...visibility, [key]: !visibility[key] };
    setVisibility(next);
    try {
      localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(next));
    } catch (err) {
      console.error("Error saving widget visibility to localStorage:", err);
    }
  };

  // widget list + labels (keys must match defaultVisibility)
  const widgetDefinitions = [
    { key: "miniWidgets", label: "Overview Charts", node: <MiniWidgets /> },
    // { key: "salesAnalytics", label: "Policy Violations", node: <SalesAnalytics /> },
    // { key: "adsBlock", label: "Ads Block", node: <AdsBlock /> },
    // { key: "earningReports", label: "Logs Analytics", node: <EarningReports /> },
    
    { key: "riskAnalysis", label: "Risk Analysis", node: <RiskAnalysis /> },
    { key: "blockedPolicies", label: "Blocked Policies", node: <BlockedPolicies /> },
     { key: "peakhours", label: "Peak Hours", node: <PeakHours /> },
     { key: "revenueAnalytics", label: "Login Sessions Map", node: <RevenueAnalytics /> },
      { key: "Topdomains", label: "TopAccessedDomains", node: <TopAccessedDomains /> },
    { key: "PolicyViolationTrends", label: "Policy Violation Trends", node: <PolicyViolationTrends /> },
    { key: "customCharts", label: "Custom Charts", node: <CustomChartsWidget /> },
  ];

  // visible widget nodes in order
  const visibleWidgets = widgetDefinitions
    .filter((w) => visibility[w.key])
    .map((w) => w.node);

  // grid styles
  const gridStyles = {
    display: "grid",
    gridTemplateColumns: `repeat(${columns}, 1fr)`,
    gap: "1rem",
    gridAutoRows: "420px",
    alignItems: "stretch",
  };

  const gridItemStyle = {
    height: "100%",
    overflow: "hidden",
  };

  const placeholdersNeeded = Math.max(0, 6 - visibleWidgets.length);

  // menu handlers
  const openMenu = (e) => setAnchorEl(e.currentTarget);
  const closeMenu = () => setAnchorEl(null);

  // helpers for select all / clear
  const selectAll = () => {
    const allOn = widgetDefinitions.reduce((acc, w) => ({ ...acc, [w.key]: true }), {});
    setVisibility(allOn);
    localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(allOn));
  };
  const clearAll = () => {
    const none = widgetDefinitions.reduce((acc, w) => ({ ...acc, [w.key]: false }), {});
    setVisibility(none);
    localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(none));
  };

  return (
    <>
      <div className={`page-content ${mode === "dark" ? "text-light" : "text-dark"}`}>
        <Container maxWidth="xl" sx={{ py: 1 }}>
          {/* Header row: Dashboard title + Widgets button on same horizontal line */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={1}
            flexWrap="wrap"
            gap={1}
          >
            <Box display="flex" alignItems="center" minWidth={0} flex={1}>
              <Typography
                variant="h5"
                component="h2"
                noWrap
                sx={{ fontWeight: 600, color: mode === "dark" ? "#fff" : "#0b1d51" }}
              >
                Dashboard
              </Typography>
            </Box>

            {/* View button with menu - aligned on the same horizontal row */}
            <Box ml={2} mt={0}>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: '#2d9cdb',
                  color: 'black',
                  '&:hover': {
                    backgroundColor: '#0b1d51 !important',
                    color: 'white',
                  },
                }}
                onClick={openMenu}
                aria-controls={menuOpen ? "widgets-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={menuOpen ? "true" : undefined}
              >
                Widgets
              </Button>
              <Menu
                id="widgets-menu"
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={closeMenu}
                PaperProps={{ sx: { minWidth: 260, maxWidth: "min(520px,92vw)", p: 1 } }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center" px={1} py={0.5}>
                  <Typography variant="subtitle2">Show Widgets</Typography>
                  <Box>
                    <Button
                      size="small"
                      onClick={() => {
                        selectAll();
                      }}
                      sx={{ textTransform: "none" }}
                    >
                      Select all
                    </Button>
                    <Button
                      size="small"
                      onClick={() => {
                        clearAll();
                      }}
                      sx={{ textTransform: "none" }}
                    >
                      Clear
                    </Button>
                  </Box>
                </Box>

                <Box sx={{ borderTop: "1px solid rgba(0,0,0,0.06)", mt: 1, pt: 1 }}>
                  {widgetDefinitions.map((w) => (
                    <MenuItem key={w.key} disableGutters dense>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!visibility[w.key]}
                            onChange={() => handleToggle(w.key)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        }
                        label={<Typography variant="body2">{w.label}</Typography>}
                        sx={{ width: "100%", margin: 0 }}
                      />
                    </MenuItem>
                  ))}
                </Box>
              </Menu>
            </Box>
          </Box>

          {/* Dashboard grid */}
          <Box sx={gridStyles}>
            {visibleWidgets.length === 0 && (
              <Box gridColumn="1 / -1" p={2}>
                <Box className="alert alert-info mb-0">
                  No widgets selected. Use the <strong>Widgets</strong> button to show Graphs and Maps.
                </Box>
              </Box>
            )}

            {visibleWidgets.map((W, idx) => (
              <Box key={idx} sx={gridItemStyle}>
                <Box sx={{ height: "100%", overflow: "auto" }}>{W}</Box>
              </Box>
            ))}

            {Array.from({ length: placeholdersNeeded }).map((_, i) => (
              <Box key={`ph-${i}`} sx={{ visibility: "hidden", height: "100%" }} />
            ))}
          </Box>
        </Container>
      </div>

      {/* Optional Dialog/Modal area (kept commented-out like your original) */}
      {/* <Modal isOpen={showDialog} toggle={handleCloseDialog} size="lg"> ... </Modal> */}
    </>
  );
};

export default Dashboard;
